// SearchResults — the rail body when a search query is active.
//
// Per designer Q17-Q19:
// - Renders one row per matching session: coach-authored title + a snippet
//   from the best-match location (title / summary / message) with the query
//   term highlighted via <mark>.
// - "+N more matches in this session" when the message-count > 1.
// - Keyboard: ↑↓ scroll through hits, Enter opens the highlighted one.
// - Empty state when the query returns nothing.
//
// Owns the search request lifecycle via a debounced effect so each keystroke
// doesn't fire an HTTP call. 250ms debounce feels responsive without
// hammering Mongo on fast typists.

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CoachProject,
  CoachSessionSearchHit,
  PatchCoachSessionRequest,
} from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { searchCoachSessions } from '../api/endpoints.js';
import { SessionMenu } from './SessionMenu.js';
import styles from './Coach.module.css';

export interface SearchResultsProps {
  query: string;
  activeSessionId: string | null;
  /** Projects list, forwarded to each hit row's kebab for the "Move to
   *  project" submenu. */
  projects: readonly CoachProject[];
  onSelect: (sessionId: string) => void;
  /** Row-level patch (archive/unarchive, move to project). */
  onPatchSession: (sessionId: string, patch: PatchCoachSessionRequest) => void;
}

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; hits: CoachSessionSearchHit[]; totalCount: number; hasMore: boolean }
  | { kind: 'error' };

const DEBOUNCE_MS = 250;

export function SearchResults({
  query,
  activeSessionId,
  projects,
  onSelect,
  onPatchSession,
}: SearchResultsProps) {
  const { t, tt } = useI18n();
  const [state, setState] = useState<SearchState>({ kind: 'idle' });
  const [cursor, setCursor] = useState<number>(0);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounced search. Re-runs whenever the query OR the include-archived
  // toggle changes so the user sees results update as they flip the filter.
  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) {
      setState({ kind: 'idle' });
      return;
    }
    setState({ kind: 'loading' });
    const handle = setTimeout(async () => {
      const result = await searchCoachSessions(q, { includeArchived });
      if (result.ok) {
        setState({
          kind: 'ok',
          hits: result.data.hits,
          totalCount: result.data.totalCount,
          hasMore: result.data.hasMore,
        });
        setCursor(0);
      } else {
        setState({ kind: 'error' });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, includeArchived]);

  const hits = state.kind === 'ok' ? state.hits : [];

  // Keyboard navigation.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (state.kind !== 'ok' || hits.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, hits.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === 'Enter') {
        const hit = hits[cursor] ?? hits[0];
        if (hit) {
          e.preventDefault();
          onSelect(hit.session.id);
        }
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.kind, hits, cursor, onSelect]);

  const headerText = useMemo(() => {
    if (state.kind === 'loading') return t('search.searching');
    if (state.kind === 'ok') return tt('search.results', { n: String(state.totalCount) });
    return '';
  }, [state, t, tt]);

  return (
    <div className={styles.searchResults} ref={listRef}>
      {(state.kind === 'loading' || state.kind === 'ok') && (
        <div className={styles.searchHeader}>
          <span>{headerText}</span>
          <label className={styles.searchArchivedToggle}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            <span>{t('search.includeArchived')}</span>
          </label>
          {state.kind === 'ok' && hits.length > 0 && (
            <span className={styles.searchHints}>{t('search.keys')}</span>
          )}
        </div>
      )}

      {state.kind === 'ok' && hits.length === 0 && (
        <div className={styles.searchEmpty}>
          <div className={styles.searchEmptyTitle}>
            {tt('search.none.title', { query })}
          </div>
          <div className={styles.searchEmptyBody}>{t('search.none.body')}</div>
          <div className={styles.searchArchivedNote}>{t('search.archivedNote')}</div>
        </div>
      )}

      {state.kind === 'ok' &&
        hits.map((hit, i) => (
          <SearchHitRow
            key={hit.session.id}
            hit={hit}
            active={hit.session.id === activeSessionId}
            highlighted={i === cursor}
            projects={projects}
            onSelect={() => onSelect(hit.session.id)}
            onPatch={(patch) => onPatchSession(hit.session.id, patch)}
          />
        ))}

      {state.kind === 'error' && (
        <div className={styles.searchEmpty}>
          <div className={styles.searchEmptyTitle}>{t('search.error.title')}</div>
          <div className={styles.searchEmptyBody}>{t('search.error.body')}</div>
        </div>
      )}
    </div>
  );
}

interface SearchHitRowProps {
  hit: CoachSessionSearchHit;
  active: boolean;
  highlighted: boolean;
  projects: readonly CoachProject[];
  onSelect: () => void;
  onPatch: (patch: PatchCoachSessionRequest) => void;
}

function SearchHitRow({
  hit,
  active,
  highlighted,
  projects,
  onSelect,
  onPatch,
}: SearchHitRowProps) {
  const { t, tt } = useI18n();
  const title = hit.session.title ?? '—';
  const extraMatches = hit.matchCount > 1 ? hit.matchCount - 1 : 0;
  const archived = hit.session.archived === true;

  // Same flex-row pattern as SessionItem: no nested buttons; main area and
  // kebab are siblings so each gets its own click target.
  return (
    <div
      className={[
        styles.siRow,
        active ? styles.siRowOn : '',
        highlighted ? styles.siRowHighlighted : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={[
          styles.searchHit,
          active ? styles.searchHitActive : '',
          highlighted ? styles.searchHitHighlighted : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={onSelect}
      >
        <div className={styles.searchHitTitle}>{title}</div>
        <div
          className={styles.searchHitSnippet}
          /* snippet carries <mark>…</mark> from the backend */
          dangerouslySetInnerHTML={{ __html: hit.snippet }}
        />
        {extraMatches > 0 && (
          <div className={styles.searchHitMore}>
            {tt('search.moreMatches', { n: String(extraMatches) })}
          </div>
        )}
        <div className={styles.searchHitChips}>
          {archived && (
            <span className={styles.archivedPill}>
              {t('history.actions.archived')}
            </span>
          )}
          {hit.session.frameworkInstanceId && (
            <span className={styles.searchHitChip}>
              {t('history.framework.badge')}
            </span>
          )}
        </div>
      </button>
      <SessionMenu
        session={hit.session}
        projects={projects}
        onPatch={onPatch}
      />
    </div>
  );
}
